import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api'; 

const ForgotPassword = () => {
  const navigate = useNavigate();

  // 'request' or 'reset'
  const [step, setStep] = useState('request'); 
  
  // Data
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  // Password Visibility
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // 🔒 Reuse Password Validation
  const validatePasswordStrength = (pwd) => {
    if (pwd.length < 8) return "Password must be at least 8 characters long.";
    let count = 0;
    if (/[A-Z]/.test(pwd)) count++; 
    if (/[a-z]/.test(pwd)) count++; 
    if (/[0-9]/.test(pwd)) count++; 
    if (/[^A-Za-z0-9]/.test(pwd)) count++; 
    if (count < 3) return "Password must include 3 of: Uppercase, Lowercase, Number, Special.";
    return null; 
  };

  // STEP 1: REQUEST RESET CODE
  const onSubmitRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      await api.post('/auth/forgot-password-init', { email });
      setStep('reset');
      setSuccessMsg(`Code sent to ${email}`);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.msg || 'Failed to send reset code.');
    }
  };

  // STEP 2: VERIFY & CHANGE PASSWORD
  const onSubmitReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return setError('Passwords do not match');

    const strengthError = validatePasswordStrength(newPassword);
    if (strengthError) return setError(strengthError);

    setLoading(true);
    setError('');

    try {
      await api.post('/auth/forgot-password-complete', { 
          email, 
          code, 
          newPassword 
      });
      
      setSuccessMsg('Password Reset Successfully! Redirecting...');
      setTimeout(() => navigate('/login'), 2000);
      
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.msg || 'Failed to reset password. Invalid code?');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.formBox}>
        <h2 style={{ textAlign: 'center', marginBottom: '10px', color: '#2c3e50' }}>
            {step === 'request' ? 'Reset Password' : 'Set New Password'}
        </h2>
        
        {step === 'request' && <p style={{textAlign:'center', fontSize:'0.9rem', color:'#7f8c8d', marginBottom:'20px'}}>Enter your email to receive a reset code.</p>}
        {step === 'reset' && <p style={{textAlign:'center', fontSize:'0.9rem', color:'#7f8c8d', marginBottom:'20px'}}>Check your email for the 6-digit code.</p>}

        {error && <div style={styles.error}>{error}</div>}
        {successMsg && <div style={styles.success}>{successMsg}</div>}

        {step === 'request' ? (
            <form onSubmit={onSubmitRequest}>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Address</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={styles.input} />
                </div>
                <button type="submit" style={styles.button} disabled={loading}>{loading ? 'Sending...' : 'Send Code'}</button>
            </form>
        ) : (
            <form onSubmit={onSubmitReset}>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Reset Code</label>
                    <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" required style={{...styles.input, textAlign:'center', letterSpacing:'4px'}} />
                </div>
                
                <div style={styles.inputGroup}>
                    <label style={styles.label}>New Password</label>
                    <div style={{position: 'relative'}}>
                        <input type={showPass ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required style={styles.input} />
                        <button type="button" onClick={() => setShowPass(!showPass)} style={styles.eyeBtn}>
                            {showPass ? <EyeSlashIcon/> : <EyeIcon/>}
                        </button>
                    </div>
                </div>

                <div style={styles.inputGroup}>
                    <label style={styles.label}>Confirm Password</label>
                    <div style={{position: 'relative'}}>
                        <input type={showConfirm ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required style={styles.input} />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={styles.eyeBtn}>
                            {showConfirm ? <EyeSlashIcon/> : <EyeIcon/>}
                        </button>
                    </div>
                </div>

                <button type="submit" style={styles.button} disabled={loading}>{loading ? 'Updating...' : 'Change Password'}</button>
            </form>
        )}

        <div style={{marginTop:'20px', textAlign:'center'}}>
            <Link to="/login" style={{color: '#3498db', textDecoration:'none', fontSize:'0.9rem'}}>Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

// SVG Icons reused from your design
const EyeIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const EyeSlashIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/></svg>;

const styles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f4f6f8' },
  formBox: { width: '400px', padding: '40px', backgroundColor: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', borderRadius: '12px' },
  inputGroup: { marginBottom: '15px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '600', color: '#34495e' },
  input: { width: '100%', padding: '12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box', outline: 'none' },
  button: { width: '100%', padding: '12px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', marginTop: '10px' },
  error: { backgroundColor: '#ffebee', color: '#c62828', padding: '10px', marginBottom: '15px', borderRadius: '6px', textAlign: 'center', fontSize: '0.9rem' },
  success: { backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '10px', marginBottom: '15px', borderRadius: '6px', textAlign: 'center', fontSize: '0.9rem' },
  eyeBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display:'flex', alignItems:'center' }
};

export default ForgotPassword;